import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { EmployeeSchema, EmployeeCreateSchema, EmployeeUpdateSchema, StoredReportSchema, WeeklyScoreSchema, PaginationQuerySchema, PaginationMetaSchema, DateRangeQuerySchema, ErrorResponseSchema, LinkAccountSchema, ServiceTypeSchema, BulkModeSchema, BulkModeResultSchema, BatchResolveSchema, BatchResolveResultSchema, } from './schemas';
const c = initContract();
export const employeesContract = c.router({
    list: {
        method: 'GET',
        path: '/employees',
        query: PaginationQuerySchema.extend({
            active: z.coerce.boolean().optional(),
            mode: z.enum(['engineering', 'standard']).optional(),
            department: z.string().optional(),
            linked: z.coerce.boolean().optional(),
        }),
        responses: {
            200: z.object({
                success: z.literal(true),
                data: z.array(EmployeeSchema),
                meta: PaginationMetaSchema.optional(),
            }),
            401: ErrorResponseSchema,
        },
        summary: 'List all employees',
    },
    getById: {
        method: 'GET',
        path: '/employees/:employeeId',
        pathParams: z.object({
            employeeId: z.string(),
        }),
        responses: {
            200: z.object({
                success: z.literal(true),
                data: EmployeeSchema,
            }),
            401: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
        summary: 'Get employee by ID',
    },
    create: {
        method: 'POST',
        path: '/employees',
        body: EmployeeCreateSchema,
        responses: {
            201: z.object({
                success: z.literal(true),
                data: EmployeeSchema,
            }),
            400: ErrorResponseSchema,
            401: ErrorResponseSchema,
        },
        summary: 'Create a new employee',
    },
    update: {
        method: 'PATCH',
        path: '/employees/:employeeId',
        pathParams: z.object({
            employeeId: z.string(),
        }),
        body: EmployeeUpdateSchema,
        responses: {
            200: z.object({
                success: z.literal(true),
                data: EmployeeSchema,
            }),
            400: ErrorResponseSchema,
            401: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
        summary: 'Update employee details',
    },
    deactivate: {
        method: 'POST',
        path: '/employees/:employeeId/deactivate',
        pathParams: z.object({
            employeeId: z.string(),
        }),
        body: z.object({}),
        responses: {
            200: z.object({
                success: z.literal(true),
                data: EmployeeSchema,
            }),
            401: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
        summary: 'Deactivate an employee',
    },
    linkAccount: {
        method: 'POST',
        path: '/employees/:employeeId/links',
        pathParams: z.object({
            employeeId: z.string(),
        }),
        body: LinkAccountSchema,
        responses: {
            200: z.object({
                success: z.literal(true),
                data: EmployeeSchema,
            }),
            400: ErrorResponseSchema,
            401: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
        summary: 'Link an external account to an employee',
    },
    unlinkAccount: {
        method: 'DELETE',
        path: '/employees/:employeeId/links/:service',
        pathParams: z.object({
            employeeId: z.string(),
            service: ServiceTypeSchema,
        }),
        body: z.object({}),
        responses: {
            200: z.object({
                success: z.literal(true),
                data: EmployeeSchema,
            }),
            401: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
        summary: 'Unlink an external account from an employee',
    },
    bulkMode: {
        method: 'POST',
        path: '/employees/bulk-mode',
        body: BulkModeSchema,
        responses: {
            200: z.object({
                success: z.literal(true),
                data: BulkModeResultSchema,
            }),
            400: ErrorResponseSchema,
            401: ErrorResponseSchema,
        },
        summary: 'Assign verification mode to multiple employees',
    },
    batchResolve: {
        method: 'POST',
        path: '/employees/batch-resolve',
        body: BatchResolveSchema,
        responses: {
            200: z.object({
                success: z.literal(true),
                data: BatchResolveResultSchema,
            }),
            400: ErrorResponseSchema,
            401: ErrorResponseSchema,
        },
        summary: 'Resolve employee identities by Slack IDs or GitHub usernames',
    },
    reports: {
        method: 'GET',
        path: '/employees/:employeeId/reports',
        pathParams: z.object({
            employeeId: z.string(),
        }),
        query: PaginationQuerySchema.merge(DateRangeQuerySchema),
        responses: {
            200: z.object({
                success: z.literal(true),
                data: z.array(StoredReportSchema),
                meta: PaginationMetaSchema.optional(),
            }),
            401: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
        summary: 'Get reports for a specific employee',
    },
    scores: {
        method: 'GET',
        path: '/employees/:employeeId/scores',
        pathParams: z.object({
            employeeId: z.string(),
        }),
        query: DateRangeQuerySchema,
        responses: {
            200: z.object({
                success: z.literal(true),
                data: z.array(WeeklyScoreSchema),
            }),
            401: ErrorResponseSchema,
            404: ErrorResponseSchema,
        },
        summary: 'Get verification scores for an employee',
    },
});
//# sourceMappingURL=employees.js.map